import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NgxErrorsModule } from '@ngspot/ngx-errors';
import { TranslateModule } from '@ngx-translate/core';
import { NgxValidators } from '@studiohyperdrive/ngx-forms';
import { NgxI18nService } from '@studiohyperdrive/ngx-i18n';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { CypressTagDirective } from '@cjm/cypress/core';
import { VLoketAppRoutePaths } from '@cjm/shared/route-paths';
import { ACMTargetGroups } from '@cjm/shared/types';
import {
	ButtonClasses,
	VloketLinkIcon,
	ButtonComponent,
	FaIconComponent,
	LayoutContainerComponent,
	LinkComponent
} from '@cjm/shared/ui/common';
import { InputWrapperComponent } from '@cjm/shared/ui/forms';
import { IsAuthenticatedDirective } from '@cjm/shared/user';

import { I18nKeys } from '../../../i18n';

import { HomeHeaderLinks } from './home-header.component.types';

@Component({
	selector: 'vloket-home-header',
	templateUrl: 'home-header.component.html',
	styleUrls: ['home-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		LayoutContainerComponent,
		IsAuthenticatedDirective,
		ReactiveFormsModule,
		FormsModule,
		NgxErrorsModule,
		InputWrapperComponent,
		CypressTagDirective,
		FaIconComponent,
		ButtonComponent,
		LinkComponent,
		TranslateModule,
		NgxSkeletonLoaderModule,
		NgTemplateOutlet
	]
})
export class HomeHeaderComponent implements OnInit {
	@HostBinding('class.c-home-header') private readonly hasPageClass: boolean = true;

	@Input({ required: true }) public userName: string;

	@Input() public primaryLinksLoading: boolean = false;
	@Input() public primaryLinks: HomeHeaderLinks;

	@Input() public secondaryLinksLoading: boolean = false;
	@Input() public secondaryLinks: HomeHeaderLinks;

	public assetUrl: string;

	public readonly buttonClasses: typeof ButtonClasses = ButtonClasses;
	public readonly appRoutePaths: typeof VLoketAppRoutePaths = VLoketAppRoutePaths;
	public readonly i18nKeys: typeof I18nKeys = I18nKeys;
	public readonly acmTargetGroups: typeof ACMTargetGroups = ACMTargetGroups;
	public readonly searchControl: FormControl<string> = new FormControl<string>('', [
		NgxValidators.wordCountValidator({ min: 0, max: 3 })
	]);

	public readonly linkIcon: VloketLinkIcon = {
		icon: 'fa-chevron-right',
		type: 'fa-solid',
		position: 'left'
	};

	constructor(
		public i18nService: NgxI18nService,
		private readonly router: Router,
		private readonly route: ActivatedRoute
	) {}

	public ngOnInit(): void {
		this.assetUrl = `/assets/img/home/home-header-image-${this.setHeaderImage()}.webp`;
	}

	/**
	 * setHeaderImage
	 *
	 * The setHeaderImage method will return a number between 1, 2 or 3 according to day of the month.
	 * So header-image-1  will be shown during the first 10 day of the month,
	 * header-image-2 during the second 10 days, etc.
	 *
	 */
	public setHeaderImage(): number {
		// Get current date
		const dayOfMonth = new Date().getDate();
		// Will return 1, 2, 3, etc. depending on the current day of the month
		return dayOfMonth <= 10 ? 1 : dayOfMonth <= 20 ? 2 : 3;
	}

	/**
	 * handleSearch
	 *
	 * The handleSearch method will be triggered when the search button is clicked.
	 * It will get the raw value of the searchControl and check if it is valid.
	 *
	 * When the value is valid, it will adjust the query params to trigger a search
	 * in the child SearchResultsPageComponent.
	 */
	public handleSearchChanges(): void {
		const searchQuery = this.searchControl.getRawValue();

		// Anything that isn't a string, should not pass. An empty string is valid.
		if (typeof searchQuery !== 'string' || searchQuery === '') {
			return;
		}

		this.router.navigate([this.appRoutePaths.Search], {
			// Navigate to the current route to prevent a page reload.
			relativeTo: this.route,
			// Set the search query param  & reset the index.
			queryParams: { zoekTerm: searchQuery },
			// Set the query param handling to merge to avoid overwriting the existing filters.
			queryParamsHandling: 'merge'
		});
	}

	/**
	 * handleSearchClear
	 *
	 * The handleSearchClear method will clear the search query.
	 */
	public handleSearchClear(): void {
		this.searchControl.patchValue('');
		this.handleSearchChanges();
	}
}
